import React, { useState, useEffect } from 'react';
import { DemographicsForm, type DemographicFormData } from './demographics-form';
import { RewardInfo } from './reward-info';
import { participantService, type ParticipantProfile } from '@/client/services/participants';
import { FixedFeedbackWidget } from '@/components/feedback';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, User, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

interface DemographicsWorkflowProps {
  onComplete: (participantId: string) => void;
  onError?: (error: string) => void;
}

export function DemographicsWorkflow({ onComplete, onError }: DemographicsWorkflowProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);
  const [existingProfile, setExistingProfile] = useState<ParticipantProfile | null>(null);

  // Check if user already has a participant profile
  useEffect(() => {
    const checkExistingProfile = async () => {
      try {
        setIsCheckingProfile(true);
        const hasProfile = await participantService.hasProfile();
        
        if (hasProfile) {
          const response = await participantService.getMyProfile();
          setExistingProfile(response.participant);
        }
      } catch (error) {
        console.error('Error checking participant profile:', error);
        // Don't show error for missing profile - that's expected for new users
      } finally {
        setIsCheckingProfile(false);
      }
    };

    checkExistingProfile();
  }, []);

  const handleSubmit = async (data: DemographicFormData) => {
    try {
      setIsLoading(true);
      const response = await participantService.createParticipant(data);
      
      toast.success('Profile created successfully!');
      onComplete(response.participantId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create participant profile';
      console.error('Error creating participant:', error);
      toast.error(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseExistingProfile = () => {
    if (existingProfile) {
      onComplete(existingProfile.id);
    }
  };

  if (isCheckingProfile) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Checking your profile...</span>
            </div>
          </CardContent>
        </Card>
        <FixedFeedbackWidget context="demographics" />
      </div>
    );
  }

  if (existingProfile) {
    return (
      <>
        <div className="max-w-2xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Profile Found
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                We found an existing participant profile for your account.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Musical Engagement:</span>
                    <p className="capitalize">{existingProfile.musicalEngagement}</p>
                  </div>
                  <div>
                    <span className="font-medium">Training Years:</span>
                    <p>{existingProfile.formalTrainingYears}</p>
                  </div>
                  <div>
                    <span className="font-medium">Listening Device:</span>
                    <p>{existingProfile.listeningDevice}</p>
                  </div>
                  <div>
                    <span className="font-medium">Environment:</span>
                    <p>{existingProfile.environment}</p>
                  </div>
                </div>
                <div>
                  <span className="font-medium text-sm">Musical Genres:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {existingProfile.musicalGenres.map((genre, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={handleUseExistingProfile} className="gap-2">
                  Continue with this profile
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <FixedFeedbackWidget context="demographics" />
      </>
    );
  }

  return (
    <>
      <div className="max-w-2xl mx-auto space-y-6">
        <RewardInfo variant="intro" />
        <DemographicsForm
          onSubmit={handleSubmit}
          onError={onError}
          isLoading={isLoading}
        />
      </div>
      <FixedFeedbackWidget context="demographics" />
    </>
  );
}